import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  isLowPowerDevice,
  isMobileBreakpoint,
  isPointWithinElement,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent, acquireDialogScrollLock, releaseDialogScrollLock } from '@theme/dialog';
import { DRAG_ZOOM_RESET_EVENT } from '@theme/drag-zoom-wrapper';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {Map<string, Promise<void>>} */
  #highResLoadPromises = new Map();

  /** @type {Set<string>} */
  #highResImagesUpgrading = new Set();

  /** @type {boolean} */
  #ownsScrollLock = false;

  /** @type {HTMLElement | null} */
  #galleryList = null;

  /** @type {number} */
  #activeMediaIndex = -1;

  /** @type {number | null} */
  #lockedThumbnailIndex = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #scrollSettleTimeout = null;

  /** @type {boolean} */
  #scrollSyncSuspended = false;

  /** @type {number} */
  #restoreScrollGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#galleryList = this.querySelector('.dialog-zoomed-gallery');
    this.#galleryList?.addEventListener('scroll', this.handleScroll);
    this.#galleryList?.addEventListener('scrollend', this.#handleGalleryScrollEnd);
    this.refs.dialog?.addEventListener('close', this.#handleDialogClosed);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#galleryList?.removeEventListener('scroll', this.handleScroll);
    this.#galleryList?.removeEventListener('scrollend', this.#handleGalleryScrollEnd);
    this.refs.dialog?.removeEventListener('close', this.#handleDialogClosed);
    if (this.#scrollSettleTimeout) clearTimeout(this.#scrollSettleTimeout);
    if (this.#scrollRaf != null) cancelAnimationFrame(this.#scrollRaf);

    if (this.refs.dialog?.open && this.#ownsScrollLock) {
      releaseDialogScrollLock();
      this.#ownsScrollLock = false;
    }
  }

  /** Safety net when the native dialog closes without closeDialog() (e.g. interrupted close()). */
  #handleDialogClosed = () => {
    this.#releaseScrollLock();
    this.#activeMediaIndex = -1;
    this.#lockedThumbnailIndex = null;
    this.#resetAllZoom();
    this.refs.dialog?.classList.remove('dialog--closed');
  };

  #acquireScrollLock() {
    if (this.#ownsScrollLock) return;

    acquireDialogScrollLock();
    this.#ownsScrollLock = true;
  }

  #releaseScrollLock() {
    if (!this.#ownsScrollLock) return;

    releaseDialogScrollLock();
    this.#ownsScrollLock = false;
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      this.#acquireScrollLock();
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
      }
    };

    const finishOpen = () => {
      open();
      this.selectThumbnail(index, { behavior: 'instant' });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      finishOpen();
      return;
    }

    await this.#ensureMediaImageReady(targetImage);
    await this.#ensureMediaImageReady(sourceImage);

    const targetImg = targetImage.querySelector('img.product-media__image');
    if (!(targetImg instanceof HTMLImageElement) || targetImg.naturalWidth === 0) {
      finishOpen();
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Ensures the base image inside a media container is loaded and decoded.
   * @param {HTMLElement} mediaContainer - The media container element.
   * @returns {Promise<boolean>} Whether an image element was found and prepared.
   */
  ensureMediaImageReady(mediaContainer) {
    return this.#ensureMediaImageReady(mediaContainer);
  }

  /**
   * @param {HTMLElement} mediaContainer - The media container element.
   * @returns {Promise<boolean>}
   */
  async #ensureMediaImageReady(mediaContainer) {
    const image = mediaContainer?.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    if (!image.complete) {
      await new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    }

    if (typeof image.decode === 'function') {
      try {
        await image.decode();
      } catch {
        // Best-effort decode
      }
    }

    return true;
  }

  /**
   * Warms the browser cache for a slide's max-resolution URL without changing the visible <img>.
   * Safe to call while scrolling — avoids the flash caused by swapping src/srcset mid-view.
   * @param {HTMLElement} mediaContainer - The media container element
   * @returns {boolean} Whether a preload was started
   */
  preloadHighResolutionImage(mediaContainer) {
    const highResolutionUrl = this.#getHighResolutionUrl(mediaContainer);
    if (!highResolutionUrl) return false;

    if (this.#highResImagesLoaded.has(highResolutionUrl) || this.#highResLoadPromises.has(highResolutionUrl)) {
      return false;
    }

    this.#ensureHighResolutionUrlLoaded(highResolutionUrl).catch(() => {});
    return true;
  }

  /**
   * Swaps a slide to its max-resolution URL. Only call when the user zooms in — not while scrolling.
   * @param {HTMLElement} mediaContainer - The media container element
   */
  upgradeHighResolutionImage(mediaContainer) {
    const image = this.#getMediaImage(mediaContainer);
    const highResolutionUrl = this.#getHighResolutionUrl(mediaContainer);
    if (!image || !highResolutionUrl) return;

    const targetWidth = this.#getHighResTargetWidth(highResolutionUrl);

    if (image.complete && image.naturalWidth >= targetWidth * 0.9) {
      this.#highResImagesLoaded.add(highResolutionUrl);
      return;
    }

    if (image.currentSrc === highResolutionUrl || image.src === highResolutionUrl) {
      this.#highResImagesLoaded.add(highResolutionUrl);
      return;
    }

    if (this.#highResImagesUpgrading.has(highResolutionUrl)) return;

    this.#highResImagesUpgrading.add(highResolutionUrl);

    this.#ensureHighResolutionUrlLoaded(highResolutionUrl)
      .then(async () => {
        if (!image.isConnected) return;

        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = highResolutionUrl;

        if (typeof image.decode === 'function') {
          try {
            await image.decode();
          } catch {
            // Best-effort decode before display swap
          }
        }
      })
      .catch(() => {
        // Keep the responsive srcset image if hi-res fails
      })
      .finally(() => {
        this.#highResImagesUpgrading.delete(highResolutionUrl);
      });
  }

  /**
   * Loads a hi-res URL once and shares the in-flight request across preload + upgrade callers.
   * @param {string} url
   * @returns {Promise<void>}
   */
  #ensureHighResolutionUrlLoaded(url) {
    if (this.#highResImagesLoaded.has(url)) {
      return Promise.resolve();
    }

    const inFlight = this.#highResLoadPromises.get(url);
    if (inFlight) return inFlight;

    /** @type {Promise<void>} */
    const promise = new Promise((resolve, reject) => {
      const loader = new Image();
      loader.onload = () => {
        this.#highResImagesLoaded.add(url);
        resolve();
      };
      loader.onerror = () => {
        reject(new Error(`Failed to load hi-res image: ${url}`));
      };
      loader.src = url;
    });

    promise.finally(() => {
      this.#highResLoadPromises.delete(url);
    });

    this.#highResLoadPromises.set(url, promise);
    return promise;
  }

  /** @deprecated Use preloadHighResolutionImage during scroll; upgradeHighResolutionImage when zooming. */
  loadHighResolutionImage(mediaContainer) {
    return this.preloadHighResolutionImage(mediaContainer);
  }

  /**
   * @param {HTMLElement} mediaContainer
   * @returns {HTMLImageElement | null}
   */
  #getMediaImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return null;

    const image = mediaContainer.querySelector('img.product-media__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  /**
   * @param {HTMLElement} mediaContainer
   * @returns {string | null}
   */
  #getHighResolutionUrl(mediaContainer) {
    const image = this.#getMediaImage(mediaContainer);
    return image?.getAttribute('data_max_resolution') ?? null;
  }

  /**
   * @param {HTMLElement[]} media
   * @param {number} index
   */
  #prepareMediaForDisplay(media, index) {
    const activeMedia = media[index];
    if (!(activeMedia instanceof HTMLElement)) return;

    this.ensureMediaImageReady(activeMedia);
    this.preloadHighResolutionImage(activeMedia);

    for (const adjacentIndex of [index - 1, index + 1]) {
      const adjacentMedia = media[adjacentIndex];
      if (!(adjacentMedia instanceof HTMLElement)) continue;

      this.ensureMediaImageReady(adjacentMedia);
      this.preloadHighResolutionImage(adjacentMedia);
    }
  }

  /**
   * @param {string} url
   * @returns {number}
   */
  #getHighResTargetWidth(url) {
    const match = url.match(/[?&]width=(\d+)/);
    return match ? Number.parseInt(match[1], 10) : 3840;
  }

  /** @type {number | null} */
  #scrollRaf = null;

  /**
   * Handles the scroll event of the gallery list to sync the active thumbnail.
   */
  handleScroll = () => {
    if (this.#lockedThumbnailIndex != null) return;
    if (this.#scrollSyncSuspended || this.#isGalleryZoomed()) return;

    if (this.#scrollRaf != null) return;

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#syncActiveMediaFromGalleryScroll({
        scrollThumbnail: true,
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      });
    });

    if (this.#scrollSettleTimeout) clearTimeout(this.#scrollSettleTimeout);
    this.#scrollSettleTimeout = setTimeout(() => {
      this.#onGalleryScrollSettled();
    }, 150);
  };

  #handleGalleryScrollEnd = () => {
    this.#onGalleryScrollSettled();
  };

  #onGalleryScrollSettled() {
    if (this.#scrollSettleTimeout) {
      clearTimeout(this.#scrollSettleTimeout);
      this.#scrollSettleTimeout = null;
    }

    if (this.#lockedThumbnailIndex == null) return;
    if (this.#isGalleryZoomed()) return;

    this.#lockedThumbnailIndex = null;

    this.#syncActiveMediaFromGalleryScroll({
      scrollThumbnail: true,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Pins the active slide index to the slide being zoomed (mobile fullscreen uses position:fixed).
   * @param {HTMLElement} mediaElement
   */
  lockActiveMediaForElement(mediaElement) {
    const { media } = this.refs;
    const slide =
      mediaElement instanceof HTMLElement && media.includes(mediaElement)
        ? mediaElement
        : media.find((item) => item.contains(mediaElement));

    if (!slide) return;

    const index = media.indexOf(slide);
    if (index >= 0) this.#activeMediaIndex = index;
  }

  /**
   * Re-aligns the gallery scroll after exiting mobile fullscreen zoom.
   * Fixed positioning removes the slide from scroll flow; without this, scroll-snap can jump.
   */
  restoreActiveSlideScroll() {
    const { media } = this.refs;
    const gallery = this.#galleryList;
    const slide = media[this.#activeMediaIndex];

    if (!(gallery instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;

    const generation = ++this.#restoreScrollGeneration;
    this.#scrollSyncSuspended = true;

    const previousSnapType = gallery.style.scrollSnapType;
    gallery.style.scrollSnapType = 'none';

    const finish = () => {
      if (generation !== this.#restoreScrollGeneration) return;

      gallery.style.scrollSnapType = previousSnapType;
      this.#scrollSyncSuspended = false;
    };

    requestAnimationFrame(() => {
      if (generation !== this.#restoreScrollGeneration) return;

      requestAnimationFrame(() => {
        if (generation !== this.#restoreScrollGeneration) return;

        try {
          slide.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
        } finally {
          requestAnimationFrame(finish);
        }
      });
    });
  }

  /** @returns {boolean} */
  #isGalleryZoomed() {
    return this.querySelector('.product-media__drag-zoom-wrapper[data-zoomed]') != null;
  }

  #resetAllZoom() {
    for (const wrapper of this.querySelectorAll('drag-zoom-wrapper')) {
      if (!(wrapper instanceof HTMLElement)) continue;

      // Reset internal zoom state via event (avoids cross-module instanceof / stale method issues).
      wrapper.dispatchEvent(new CustomEvent(DRAG_ZOOM_RESET_EVENT, { bubbles: false }));

      // Always reset visual/snap state even if the wrapper module is stale or not yet upgraded.
      wrapper.style.setProperty('--drag-zoom-scale', '1');
      wrapper.style.setProperty('--drag-zoom-translate-x', '0px');
      wrapper.style.setProperty('--drag-zoom-translate-y', '0px');
      wrapper.toggleAttribute('data-dragging', false);
      wrapper.toggleAttribute('data-zoomed', false);
    }
  }

  /**
   * @param {number} index
   * @param {{ scrollThumbnail?: boolean, behavior?: ScrollBehavior }} [options]
   */
  #activateMediaIndex(index, options = {}) {
    const { scrollThumbnail = true, behavior = 'instant' } = options;
    const { thumbnails } = this.refs;

    if (!thumbnails?.children.length || index < 0 || index >= thumbnails.children.length) return;

    if (index !== this.#activeMediaIndex) {
      this.#resetAllZoom();
      this.#activeMediaIndex = index;
    }

    this.#setActiveThumbnail(index, { scrollThumbnail, behavior });
  }

  /**
   * Closes the lightbox when clicking outside the visible image pixels.
   * @param {MouseEvent} event
   */
  handleGalleryClick(event) {
    // Theme on:click handlers proxy event.target to the delegated element (the gallery ul),
    // so use composedPath for the element that was actually clicked.
    const path = event.composedPath();
    const clickTarget = path[0] instanceof Element ? path[0] : event.target;
    if (!(clickTarget instanceof Element)) return;

    if (
      clickTarget.closest('.dialog-thumbnails-list__thumbnail') ||
      clickTarget.closest('.dialog-zoomed-gallery__close-button')
    ) {
      return;
    }

    const image = clickTarget.closest('.product-media-container--image')?.querySelector('img.product-media__image');
    if (image instanceof HTMLImageElement && isPointWithinElement(event.clientX, event.clientY, image)) {
      return;
    }

    event.preventDefault();
    this.close();
  }

  /**
   * @param {{ scrollGallery?: boolean, scrollThumbnail?: boolean, behavior?: ScrollBehavior }} [options]
   */
  #syncActiveMediaFromGalleryScroll(options = {}) {
    const { scrollGallery = false, scrollThumbnail = true, behavior = 'instant' } = options;
    const { media } = this.refs;
    const root = this.#galleryList;

    if (!root || !media?.length) return;

    const activeIndex = getActiveMediaIndexFromScroll(media, root);

    if (activeIndex < 0) return;

    if (this.#lockedThumbnailIndex != null && activeIndex !== this.#lockedThumbnailIndex) {
      return;
    }

    if (activeIndex === this.#activeMediaIndex) return;

    this.#activateMediaIndex(activeIndex, { scrollThumbnail, behavior });

    const activeMedia = media[activeIndex];
    if (scrollGallery && activeMedia) {
      activeMedia.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'nearest',
      });
    }

    this.#prepareMediaForDisplay(media, activeIndex);
  }

  /**
   * @param {number} index
   * @param {{ scrollThumbnail?: boolean, behavior?: ScrollBehavior }} [options]
   */
  #setActiveThumbnail(index, options = {}) {
    const { scrollThumbnail = true, behavior = 'instant' } = options;
    const { thumbnails } = this.refs;

    if (!thumbnails?.children.length || index < 0 || index >= thumbnails.children.length) return;

    const targetThumbnail = thumbnails.children[index];
    if (!(targetThumbnail instanceof HTMLElement)) return;

    const isAlreadySelected = targetThumbnail.getAttribute('aria-selected') === 'true';

    if (!isAlreadySelected) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });
    }

    if (scrollThumbnail && !isAlreadySelected) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior,
        block: 'center',
        inline: 'center',
      });
    }
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!dialog?.open) return;

    // Mobile: skip the view-transition close path — IntersectionObserver + VT close can
    // hang before closeDialog(), leaving html[scroll-lock] and the modal top layer active.
    if (!supportsViewTransitions() || isLowPowerDevice() || isMobileBreakpoint()) {
      this.closeDialog();
      return;
    }

    try {
      const mostVisibleElement = await getMostVisibleElement(media, this.#galleryList);
      const activeIndex = media.indexOf(mostVisibleElement);
      const itemTransitionName = `gallery-item-close`;

      const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
        this.closest('media-gallery')
      );

      const slideshowActive = mediaGallery?.presentation === 'carousel';

      const slide = slideshowActive
        ? mediaGallery.slideshow?.slides?.[activeIndex]
        : mediaGallery?.media?.[activeIndex];

      if (!slide) {
        this.closeDialog();
        return;
      }

      const focalPoint = slide.dataset.focalPoint;
      if (focalPoint) {
        document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
      }

      dialog.classList.add('dialog--closed');

      await onAnimationEnd(this.refs.thumbnails);

      mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

      await startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.setProperty('view-transition-name', itemTransitionName);
        this.closeDialog();
      });

      slide.style.removeProperty('view-transition-name');
      document.documentElement.style.removeProperty('--gallery-media-focal-point');
    } catch (error) {
      console.error(error);
      if (dialog.open) this.closeDialog();
    } finally {
      dialog.classList.remove('dialog--closed');
    }
  }

  closeDialog() {
    const { dialog } = this.refs;

    this.#restoreScrollGeneration++;
    if (this.#galleryList instanceof HTMLElement && this.#scrollSyncSuspended) {
      this.#galleryList.style.removeProperty('scroll-snap-type');
      this.#scrollSyncSuspended = false;
    }

    if (this.#activeMediaIndex >= 0) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(this.#activeMediaIndex));
    }

    this.#releaseScrollLock();
    this.#activeMediaIndex = -1;
    this.#lockedThumbnailIndex = null;
    this.#resetAllZoom();
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.#lockedThumbnailIndex = index;
    await this.selectThumbnail(index, { behavior, fromThumbnailClick: true });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.preloadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} [options.behavior] - The behavior of the scroll.
   * @param {boolean} [options.fromThumbnailClick] - Whether the selection came from a thumbnail click.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media } = this.refs;
    const behavior = options.behavior ?? 'smooth';
    const fromThumbnailClick = options.fromThumbnailClick ?? false;

    this.#activateMediaIndex(index, {
      scrollThumbnail: !fromThumbnailClick,
      behavior: fromThumbnailClick ? 'instant' : behavior,
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'nearest',
      });

      this.#prepareMediaForDisplay(media, index);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Returns the index of the slide with the largest visible area in the scroll container.
 * @param {HTMLElement[]} elements
 * @param {HTMLElement | null} root
 * @returns {number}
 */
function getActiveMediaIndexFromScroll(elements, root) {
  if (!(root instanceof HTMLElement) || !elements.length) return -1;

  const rootRect = root.getBoundingClientRect();
  let activeIndex = 0;
  let maxVisibleArea = 0;

  elements.forEach((slide, index) => {
    if (!(slide instanceof HTMLElement)) return;

    const rect = slide.getBoundingClientRect();
    const visibleWidth = Math.min(rect.right, rootRect.right) - Math.max(rect.left, rootRect.left);
    const visibleHeight = Math.min(rect.bottom, rootRect.bottom) - Math.max(rect.top, rootRect.top);

    if (visibleWidth <= 0 || visibleHeight <= 0) return;

    const visibleArea = visibleWidth * visibleHeight;
    if (visibleArea > maxVisibleArea) {
      maxVisibleArea = visibleArea;
      activeIndex = index;
    }
  });

  return activeIndex;
}

/**
 * Get the most visible element from a list of elements within a scroll container.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @param {HTMLElement | null} [root] - The scroll container used as the intersection root.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements, root = null) {
  const targets = elements.filter((element) => element instanceof HTMLElement);

  if (!targets.length) {
    return Promise.resolve(/** @type {HTMLElement} */ (elements[0]));
  }

  const defaultTarget = /** @type {HTMLElement} */ (targets[0]);

  return new Promise((resolve) => {
    let settled = false;

    /** @param {HTMLElement} element */
    const finish = (element) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(fallbackTimeout);
      resolve(element);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.length) return;

        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        finish(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        root,
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of targets) {
      observer.observe(element);
    }

    const fallbackTimeout = setTimeout(() => {
      if (root instanceof Element) {
        const scrollTop = root.scrollTop;
        let closest = defaultTarget;
        let closestDistance = Infinity;

        for (const element of targets) {
          const distance = Math.abs(element.offsetTop - scrollTop);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = element;
          }
        }

        finish(closest);
        return;
      }

      finish(defaultTarget);
    }, 500);
  });
}
